import React, { useState } from 'react';
import { useDispatch } from 'react-redux';

import {
  AsyncSelect,
  Button,
  FileDropzone,
  Input,
  Modal,
  QtySelector,
  SaveButton,
  Select,
  Textarea,
  useIsAdmin,
  useValidator
} from 'components';
import { notifyApiError, notifyCommon } from 'components/layout/Toasts';

import { ServicesApi } from 'src/api';
import CreateServiceForm from 'src/features/Services/components/ServicersList/components/CreateServiceForm';

import { refreshServices } from '../../../Services/actions';

import style from './EventForm.module.scss';

const options = [
  { label: 'Naprawa', value: 'FIX' },
  { label: 'Serwis', value: 'SERVICE' }
];

const eventStatusesOptions = [
  { label: 'Zgłoszony', value: 'REPORTED' },
  { label: 'Wysłany do serwisu', value: 'SENT_TO_SERVICE' },
  { label: 'Oczekuje', value: 'PENDING' },
  { label: 'Zakończony', value: 'COMPLETED' }
];

const defaultService = {
  event_date: null,
  servicer: null,
  comment: '',
  type: options[1],
  file: null,
  status: null,
  expenses: 0
};

const EventForm = ({ eventData, onClose, refreshCallback, serviceId, selectedCompany }) => {
  const dispatch = useDispatch();
  const isAdmin = useIsAdmin();

  const validator = useValidator();
  const [isLoading, setIsLoading] = useState(false);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [servicersVersion, setServicersVersion] = useState(0);
  const [event, setEvent] = useState(eventData || defaultService);

  const onChange = (e) => {
    setEvent((prev) => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const onChangeHandler = (value, key) => {
    setEvent((prev) => ({
      ...prev,
      [key]: value
    }));
  };

  const addOrEditServiceHandler = async (e, type) => {
    e.preventDefault();

    if (!validator.allValid()) {
      return validator.showMessages();
    }

    const params = {
      service_id: serviceId,
      ...(type === 'edit' && { event_id: eventData.id }),
      ...(isAdmin && { company_id: selectedCompany }),
      ...event,
      servicer_id: event.servicer.value,
      type: event.type.value,
      status: event.status.value
    };

    const apiCallback = type === 'edit' ? ServicesApi.editEvent : ServicesApi.addEvent;

    try {
      setIsLoading(true);
      await apiCallback(params);
      if (onClose) onClose();
      if (refreshCallback) refreshCallback();
      dispatch(refreshServices());
      notifyCommon([`Zdarzenie zostało pomyślnie ${type === 'add' ? 'dodane' : 'zaktualizowane'}.`]);
    } catch (err) {
      notifyApiError(err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <form
        className={style.container}
        onSubmit={(e) => addOrEditServiceHandler(e, eventData ? 'edit' : 'add')}
      >
        <div className={style.columns}>
          {!eventData && (
            <Select
              label={'Typ'}
              options={options}
              rule={'required'}
              value={event.type}
              isSearchable={false}
              validator={validator}
              onChange={(value) => onChangeHandler(value, 'type')}
            />
          )}
          <Input
            type='date'
            id='event_date'
            name='event_date'
            rule={'required'}
            onChange={onChange}
            label={event.type.value === 'SERVICE' ? 'Data serwisu' : 'Data naprawy'}
            validator={validator}
            value={event.event_date || ''}
          />
        </div>
        <div className={style.columns}>
          <Select
            label={'Status'}
            options={eventStatusesOptions}
            placeholder={'Wybierz..'}
            rule={'required'}
            value={
              typeof event.status === 'string'
                ? eventStatusesOptions.find((status) => status.value === event.status)
                : event.status
            }
            isSearchable={false}
            validator={validator}
            onChange={(status) => onChangeHandler(status, 'status')}
          />
          <QtySelector
            label={'Koszt'}
            setState={(val) => onChangeHandler(val, 'expenses')}
            wrapperStyle={style.quantityInput}
            state={event.expenses}
            validator={validator}
            rule={'only_positive'}
            id={'expenses'}
            suffix={'zł'}
            width={'100%'}
            priceInput
            hideArrows
          />
        </div>
        <div className={style.servicerWrapper}>
          <AsyncSelect
            valueKey={'id'}
            labelKey={'name'}
            rule={'required'}
            label={'Serwisant'}
            id={'select-product'}
            validator={validator}
            value={event.servicer}
            queryParams={{ servicersVersion, ...(isAdmin && { company_id: selectedCompany }) }}
            placeholder={'Wybierz serwisanta...'}
            apiCallback={ServicesApi.getServicersList}
            onChange={(value) => onChangeHandler(value, 'servicer')}
          />
          {!eventData && (
            <SaveButton
              type={'add'}
              onClick={() => setIsCreateOpen(true)}
              className={style.addButton}
              htmlType={'button'}
            />
          )}
        </div>
        <Textarea
          label={'Powód'}
          rule={'required'}
          value={event.comment}
          validator={validator}
          setValue={(value) => onChangeHandler(value, 'comment')}
        />
        {!eventData && (
          <FileDropzone
            id={'logo'}
            name={'logo'}
            value={event.file}
            label={'Załącznik'}
            onChange={(value) => onChangeHandler(value, 'file')}
          />
        )}
        <Button
          isLoading={isLoading}
          className={style.button}
          label={eventData ? 'Zapisz' : 'Dodaj'}
        />
      </form>
      <Modal
        visible={isCreateOpen}
        title={'Dodaj serwisanta'}
        onClose={() => setIsCreateOpen(false)}
      >
        <CreateServiceForm
          onClose={() => setIsCreateOpen(false)}
          refreshCallback={() => setServicersVersion((prev) => prev + 1)}
        />
      </Modal>
    </>
  );
};

export default EventForm;
